import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import {
  type AIProviderId,
  getAIProvider,
  isManagedChatModel,
  managedAIProviderIdForModel,
  resolveAIProviderId,
} from "../aiProviders";
import {
  attachmentSizeLimit,
  resolveAttachmentMediaType,
  volumeAttachmentSizeError,
} from "../attachmentPolicy";
import { userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { clientGeneratableIdSchema } from "../id";

export const attachmentSchema = z.object({
  id: clientGeneratableIdSchema,
  fileName: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number(),
});

type AttachmentInput = {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
};

async function resolveCurrentAIProviderId(
  tx: {
    run: Pick<Transaction<Schema>, "run">["run"];
  },
  chatModel?: string
): Promise<AIProviderId | null> {
  if (
    chatModel &&
    isManagedChatModel(chatModel) &&
    managedAIProviderIdForModel(chatModel) === "openai"
  ) {
    return "openai";
  }
  const [config] = (await tx.run(
    zql.aiProviders.where("active", true).limit(1)
  )) as Array<{ provider?: string | null; baseUrl?: string | null }>;
  return config ? resolveAIProviderId(config) : null;
}

export async function assertAttachmentsSupported(
  tx: {
    run: Pick<Transaction<Schema>, "run">["run"];
  },
  attachments?: AttachmentInput[],
  chatModel?: string
) {
  if (!attachments?.length) return;

  const providerId = await resolveCurrentAIProviderId(tx, chatModel);
  if (!providerId) return;
  const providerLabel = getAIProvider(providerId).label;

  for (const attachment of attachments) {
    const mediaType = resolveAttachmentMediaType(
      { name: attachment.fileName, type: attachment.mediaType },
      providerId
    );
    if (!mediaType) {
      throw userFacingError(
        `${providerLabel} does not support ${attachment.fileName} (${attachment.mediaType})`
      );
    }

    if (attachment.sizeBytes > attachmentSizeLimit(mediaType)) {
      throw userFacingError(
        `${attachment.fileName} exceeds the ${providerLabel} attachment limit for ${mediaType}`
      );
    }
  }
}

// Uploads are copied into a volume rather than sent to the model, so only the
// volume caps apply.
export function assertChatAttachmentsAllowed(attachments?: AttachmentInput[]) {
  for (const attachment of attachments ?? []) {
    const error = volumeAttachmentSizeError(attachment);
    if (error) throw userFacingError(error);
  }
}
